import { Badge } from "~/components/legacy/common/badge"
import { Card } from "~/components/legacy/common/card"
import { H6 } from "~/components/legacy/common/heading"
import { ShieldIcon } from "~/components/ui/icons"
import { cx } from "~/lib/legacy-utils"
import { parseRuleEvidence } from "./checker-scoring"
import type { CheckerPolicy, CheckerResult } from "./checker-types"

const familyDomains: Record<string, string> = {
  Amazon: "amazon.com",
  Anthropic: "anthropic.com",
  Apple: "apple.com",
  ByteDance: "bytedance.com",
  "Common Crawl": "commoncrawl.org",
  Google: "google.com",
  Meta: "meta.com",
  "Microsoft Bing": "bing.com",
  OpenAI: "openai.com",
  Perplexity: "perplexity.ai",
}

// logo.dev publishable token. Sourced from env so it can be rotated without a
// rebuild; when unset we fall back to the generic shield icon below.
const logoDevToken = process.env.NEXT_PUBLIC_LOGODEV_TOKEN

const policyBadgeVariant: Record<CheckerPolicy, "danger" | "success" | "warning"> = {
  allow: "success",
  disallow: "danger",
  unknown: "warning",
}

const policyBadgeBorder: Record<CheckerPolicy, string> = {
  allow: "border border-green-200 dark:border-green-900",
  disallow: "border border-red-200 dark:border-red-900",
  unknown: "border border-orange-200 dark:border-orange-900",
}

function getLogoUrl(domain: string) {
  const params = new URLSearchParams({ format: "png", size: "64" })
  if (logoDevToken) {
    params.set("token", logoDevToken)
  }

  return `https://img.logo.dev/${domain}?${params.toString()}`
}

function CrawlerLogo({ family }: Readonly<{ family: string }>) {
  const domain = logoDevToken ? familyDomains[family] : undefined

  return (
    <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-card text-muted-foreground">
      {domain ? (
        <img
          src={getLogoUrl(domain)}
          alt={`${family} logo`}
          width={24}
          height={24}
          className="size-6 object-contain"
          loading="lazy"
        />
      ) : (
        <ShieldIcon className="size-4" aria-hidden />
      )}
    </span>
  )
}

export function CrawlerCard({
  labels,
  result,
}: Readonly<{
  labels: Record<CheckerPolicy, string>
  result: CheckerResult
}>) {
  const evidence = parseRuleEvidence(result)
  const isAllowed = result.policy === "allow"

  return (
    <Card hover={false} className="gap-3 rounded-xl bg-background p-4">
      <div className="flex w-full items-center gap-3">
        <CrawlerLogo family={result.family} />
        <H6 as="h3" className="min-w-0 flex-1 truncate">
          {result.token}
        </H6>
        <span
          className={cx(
            "size-2 shrink-0 rounded-full",
            isAllowed ? "bg-green-600 dark:bg-green-500" : "bg-red-600 dark:bg-red-500",
          )}
          aria-hidden
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge
          variant={policyBadgeVariant[result.policy]}
          size="lg"
          className={policyBadgeBorder[result.policy]}
        >
          {labels[result.policy]}
        </Badge>
        <Badge variant="outline" size="lg">
          {result.family}
        </Badge>
      </div>

      <div className="mt-auto flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-dashed pt-3 font-mono text-xs text-muted-foreground">
        <span className="shrink-0">{evidence.lineLabel}</span>
        <span className="min-w-0 break-all">
          <span
            className={
              evidence.directive === "allow"
                ? "text-green-700 dark:text-green-400"
                : "text-red-700 dark:text-red-400"
            }
          >
            {evidence.directive}:
          </span>{" "}
          {evidence.path}
        </span>
      </div>
    </Card>
  )
}
